import { APP_ID, ErrorHandler, Injector, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { provideClientHydration, withNoIncrementalHydration } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import localeENGB from '@angular/common/locales/en-GB';
import localeDECH from '@angular/common/locales/de-CH';
import localeFRCH from '@angular/common/locales/fr-CH';
import localeITCH from '@angular/common/locales/it-CH';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { AppConfigService } from './core/services/app-config.service';
import { LanguageService } from './core/services/language.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData, DatePipe } from '@angular/common';
import { LoaderInterceptor } from './core/interceptors/loader.interceptor';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateMatPaginatorIntl } from './core/common/translate-mat-paginator.intl';
import { CustomTranslateHttpLoader } from './core/loaders/custom-translate-http-loader';
import { AssetBrowserLoader } from './core/loaders/asset-browser.loader';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { BrowserErrorHandler } from './core/handlers/browser.error.handler';
import { UniversalStorage } from './core/storages/universal.storage';
import { AssetLoader } from './core/loaders/asset.loader';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { WindowBrowserAccessor } from './core/accessors/window-browser.accessor';
import { WindowAccessor } from './core/accessors/window.accessor';
import { ObservableBrowserLoader } from './core/loaders/observable-browser.loader';
import { ObservableLoader } from './core/loaders/observable.loader';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

registerLocaleData(localeENGB);
registerLocaleData(localeDECH);
registerLocaleData(localeFRCH);
registerLocaleData(localeITCH);

export const initFactory = (appConfigService: AppConfigService, languageService: LanguageService) => {
  // Resolve app-config first because languageservice depends on it
  return () => appConfigService.init()
    .then(value => languageService.init());
};

export function getApiUrl(appConfigService: AppConfigService): string {
  return appConfigService.getConfig().apiUrl;
}

export function getCurrentLanguageCode(languageService: LanguageService): string {
  return languageService.currentLanguageCode;
}

export function customTranslateHttpLoaderFactory(injector: Injector) {
  return new CustomTranslateHttpLoader(injector);
}

const matFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
  hideRequiredMarker: true
};

@NgModule({
  declarations: [
    AppComponent
  ],
  exports: [SharedModule],
  bootstrap: [AppComponent],
  imports: [AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: customTranslateHttpLoaderFactory,
        deps: [Injector],
      },
    })],
  providers: [
    provideAppInitializer(() => {
        const initializerFn = (initFactory)(inject(AppConfigService), inject(LanguageService));
        return initializerFn();
      }),
    {
      provide: AssetLoader,
      useClass: AssetBrowserLoader
    },
    { provide: LOCALE_ID, useFactory: getCurrentLanguageCode, deps: [LanguageService] },
    LanguageService,
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    {
      provide: MatPaginatorIntl, deps: [TranslateService],
      useFactory: (translateService: TranslateService) => new TranslateMatPaginatorIntl(translateService)
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useClass: BrowserErrorHandler
    },
    UniversalStorage,
    {
      provide: WindowAccessor,
      useClass: WindowBrowserAccessor
    },
    {
      provide: ObservableLoader,
      useClass: ObservableBrowserLoader
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: matFormFieldDefaultOptions
    },
    // Pipes
    DatePipe,
    { provide: APP_ID, useValue: 'serverApp' },
    // Transfer cached http response from server to client and prevent duplicate requests
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
  ]
})
export class AppModule { }
